/**
 * ROTAS69 option dropdown — behaviour for snippets/r69-option-select.
 *
 * Every listener is DELEGATED on the document. The theme re-renders the whole variant picker on
 * each variant change and morphs it in, so anything wired per-element has to be re-wired after
 * every swap; delegation sidesteps that entirely.
 *
 * The widget never owns the selection. It writes the chosen value into the native control that
 * snippets/variant-main-picker left in the DOM (a radio group in "buttons" mode, a <select> in
 * "dropdowns" mode) and fires a bubbling `change`, which is exactly what <variant-picker> listens
 * for. The variant added to the cart is therefore unchanged by this widget existing.
 *
 * State classes are r69dd-open / r69dd-selected / r69dd-soldout — deliberately not `is-open`,
 * which is an argument of the theme's header :has() rules and would make every toggle pay a
 * document-wide style recalc.
 */
(() => {
  if (window.__r69OptionSelect) return;
  window.__r69OptionSelect = true;

  const OPEN = 'r69dd-open';

  const scopeOf = (widget) => widget.closest('variant-picker') || document;

  /** The real form control this widget speaks for. */
  function nativeControl(widget) {
    const index = widget.getAttribute('data-r69dd-for');
    return scopeOf(widget).querySelector(`[data-r69dd-host="${index}"]`);
  }

  /** One interface over the two shapes the native control can take. */
  function adapter(host) {
    if (!host) return null;

    const select = host.querySelector('select');
    if (select) {
      return {
        get: () => select.value,
        set(value) {
          if (select.value === value) return;
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        },
        available(value) {
          const option = Array.from(select.options).find((o) => o.value === value);
          return option ? !option.disabled : false;
        },
      };
    }

    const radios = Array.from(host.querySelectorAll('input[type="radio"]'));
    if (!radios.length) return null;
    const find = (value) => radios.find((r) => r.value === value);
    return {
      get: () => (radios.find((r) => r.checked) || radios[0] || {}).value || '',
      set(value) {
        const radio = find(value);
        if (!radio || radio.checked) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      },
      available(value) {
        const radio = find(value);
        if (!radio) return false;
        if (radio.getAttribute('aria-disabled') === 'true') return false;
        return radio.dataset.optionAvailable !== 'false';
      },
    };
  }

  function options(widget) {
    return Array.from(widget.querySelectorAll('[data-r69dd-opt]'));
  }

  function close(widget, refocus) {
    if (!widget.classList.contains(OPEN)) return;
    widget.classList.remove(OPEN);
    widget.querySelector('.r69dd__panel').hidden = true;
    const trigger = widget.querySelector('[data-r69dd-trigger]');
    trigger.setAttribute('aria-expanded', 'false');
    if (refocus) trigger.focus();
  }

  function closeAll(except) {
    document.querySelectorAll(`.${OPEN}`).forEach((w) => {
      if (w !== except) close(w, false);
    });
  }

  function open(widget) {
    closeAll(widget);
    widget.classList.add(OPEN);
    widget.querySelector('.r69dd__panel').hidden = false;
    widget.querySelector('[data-r69dd-trigger]').setAttribute('aria-expanded', 'true');
    const focusTarget =
      widget.querySelector('.r69dd-selected:not(.r69dd-soldout)') ||
      options(widget).find((o) => !o.classList.contains('r69dd-soldout'));
    if (focusTarget) focusTarget.focus();
  }

  /** Bring the widget back in line with the native control. */
  function sync(widget) {
    const native = adapter(nativeControl(widget));
    if (!native) return;
    const current = native.get();

    options(widget).forEach((option) => {
      const isCurrent = option.getAttribute('data-r69dd-value') === current;
      option.classList.toggle('r69dd-selected', isCurrent);
      option.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      option.classList.toggle('r69dd-soldout', !native.available(option.getAttribute('data-r69dd-value')));
    });

    const selected = options(widget).find((o) => o.getAttribute('data-r69dd-value') === current);
    const label = selected ? selected.querySelector('.r69dd__opt-text').textContent : current;
    const currentEl = widget.querySelector('[data-r69dd-current]');
    if (currentEl.textContent !== label) currentEl.textContent = label;
    widget
      .querySelector('[data-r69dd-trigger]')
      .classList.toggle('r69dd-soldout', !native.available(current));
  }

  function pick(widget, option) {
    if (option.classList.contains('r69dd-soldout')) return;
    const native = adapter(nativeControl(widget));
    if (native) native.set(option.getAttribute('data-r69dd-value'));
    sync(widget);
    close(widget, true);
  }

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-r69dd-opt]');
    if (option) {
      event.preventDefault();
      pick(option.closest('[data-r69dd]'), option);
      return;
    }

    const trigger = event.target.closest('[data-r69dd-trigger]');
    if (trigger) {
      event.preventDefault();
      const widget = trigger.closest('[data-r69dd]');
      if (widget.classList.contains(OPEN)) close(widget, true);
      else open(widget);
      return;
    }

    closeAll(event.target.closest('[data-r69dd]'));
  });

  document.addEventListener('keydown', (event) => {
    const widget = event.target.closest('[data-r69dd]');
    if (!widget) return;

    if (event.key === 'Escape') {
      close(widget, true);
      return;
    }

    const onTrigger = !!event.target.closest('[data-r69dd-trigger]');
    if (onTrigger && !widget.classList.contains(OPEN) && ['ArrowDown', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      open(widget);
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const selectable = options(widget).filter((o) => !o.classList.contains('r69dd-soldout'));
    if (!selectable.length) return;
    event.preventDefault();
    const at = selectable.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? at + 1 : at - 1;
    selectable[(next + selectable.length) % selectable.length].focus();
  });

  // The variant can also change from a swatch, the sticky bar or the ring picker.
  document.addEventListener('change', (event) => {
    const picker = event.target.closest('variant-picker');
    if (!picker) return;
    picker.querySelectorAll('[data-r69dd]').forEach(sync);
  });

  const start = () => document.querySelectorAll('[data-r69dd]').forEach(sync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // quick-add injects the picker markup on a collection/search page; re-sync any freshly-inserted
  // dropdowns so their trigger label + sold-out state are correct. (The click/keydown/change
  // listeners above are delegated on the document, so they already cover injected widgets.)
  document.addEventListener('r69:rewire', (event) => {
    const root = (event.detail && event.detail.root) || document;
    root.querySelectorAll('[data-r69dd]').forEach(sync);
  });
})();
